<template>
    <AdminPageInner v-if="loaded" :breadcrumbs="breadcrumbs">
        <template #heading>Records</template>
        <template #links>
            <router-link
                v-if="$can('create', 'CourseRecord')"
                :to="{name: 'admin.courses.session-groups.sessions.records.bulk', params: $route.params}">
                Bulk Record
            </router-link>
        </template>

        <div class="sm:flex sm:items-center sm:space-x-4">
            <p>Filters:</p>
            <Select :options="statusFilters" v-model="status" class="w-full max-w-sm"/>
        </div>

        <PaginationWrapper
            ref="pagination"
            :rows="rows"
            :constraints="constraints"
            :meta="meta"
            class="mt-6">
            <DataComponent
                class="w-full mt-6"
                :data="rows"
                :initialSort="initialSort"
                @sort="onSort">
                <DataField name="user_name" label="Name" class="md:w-1/4"/>
                <DataField name="status" label="Status" class="md:w-1/4" :sortable="false">
                    <template v-slot="{row}">
                        <div class="relative flex items-center">
                            <p v-if="row.attended" class="text-green-600">ATTENDED</p>
                            <p v-else-if="row.attended === false" class="text-red-600">ABSENT</p>
                            <p v-else>PENDING</p>

                            <template v-if="row.application_status === ApplicationStatus.Cancelled">
                                <p class="ml-2 italic text-gray-400">*Cancelled</p>
                            </template>
                        </div>
                    </template>
                </DataField>
                <DataField name="feedback" class="w-1/4" :sortable="false">
                    <template v-slot="{row}">
                        <p
                            class="cursor-pointer"
                            :class="{'line-clamp-3': !row.expandFeedback}"
                            @click.prevent="row.expandFeedback = !row.expandFeedback">
                            {{ row.feedback || '-' }}
                        </p>
                    </template>
                </DataField>
                <DataField
                    name="actions"
                    class="md:w-40"
                    :sortable="false"
                    :showLabel="false"
                    :showResponsiveLabel="false">
                    <template v-slot="{row}">
                        <div class="flex justify-around md:justify-end items-center">
                            <router-link
                                v-if="!row.id && $can('create', 'CourseRecord')"
                                class="text-indigo-600 hover:text-indigo-900 mr-4"
                                :to="createRouteTo(row)">
                                Create
                            </router-link>
                            <router-link
                                v-if="row.id && row.user_can.update"
                                class="text-indigo-600 hover:text-indigo-900 mr-4"
                                :to="editRouteTo(row)">
                                Edit
                            </router-link>
                        </div>
                    </template>
                </DataField>
            </DataComponent>
        </PaginationWrapper>
    </AdminPageInner>
</template>

<script>

import axios from "axios";
import {CourseApplicationStatus as ApplicationStatus, CourseRecordStatus as RecordStatus} from "@/Lib/Enums";
import AdminPageInner from "@/Components/Layout/AdminPageInner";
import PaginationWrapper from "@/Components/Layout/PaginationWrapper";
import PaginationPage from "@/Mixins/PaginationPage";
import Select from "@/Components/Form/Select";
import {DataComponent, DataField} from "@/Components/DataComponent";
import Button from "@/Components/Button";

export default {

    name: "CourseRecordsIndexPage",

    mixins: [PaginationPage],

    data() {
        return {
            ApplicationStatus,
            RecordStatus,
            sessionGroup: {},
            statusFilters: [
                {label: "All", value: null},
                {label: "Pending", value: RecordStatus.Pending},
                {label: "Attended", value: RecordStatus.Attended},
                {label: "Absent", value: RecordStatus.Absent},
                {label: "Cancelled", value: RecordStatus.Cancelled},
            ],
            editing: [],
            processing: []
        };
    },

    components: {
        AdminPageInner,
        PaginationWrapper,
        DataComponent,
        DataField,
        Select,
        Button,
    },

    methods: {
        setData({records, course}) {
            PaginationPage.methods.setData.call(this, records);

            if (course) {
                this.course = course;
            }
        },
        createRouteTo(record) {
            return {
                name: 'admin.courses.session-groups.sessions.records.create',
                params: {
                    courseId: this.$route.params.courseId,
                    courseSessionGroupId: this.$route.params.courseSessionGroupId,
                    courseSessionId: this.$route.params.courseSessionId,
                    courseApplicationId: record.course_application_id
                }
            };
        },
        showRouteTo(record) {
            return {
                name: 'admin.courses.session-groups.sessions.records.show',
                params: {
                    courseId: this.$route.params.courseId,
                    courseSessionGroupId: this.$route.params.courseSessionGroupId,
                    courseSessionId: this.$route.params.courseSessionId,
                    courseRecordId: record.id
                }
            };
        },
        editRouteTo(record) {
            return {
                name: 'admin.courses.session-groups.sessions.records.edit',
                params: {
                    courseId: this.$route.params.courseId,
                    courseSessionGroupId: this.$route.params.courseSessionGroupId,
                    courseSessionId: this.$route.params.courseSessionId,
                    courseRecordId: record.id
                }
            };
        }
    },

    computed: {
        breadcrumbs() {
            return [
                {
                    label: 'Admin', to: {name: 'admin'}
                },
                {
                    label: 'Courses', to: {name: 'admin.courses.index'}
                },
                {
                    label: `${this.course.title || 'Course'}`,
                    to: {name: 'admin.courses.show', params: {courseId: this.$route.params.courseId}}
                },
                {
                    label: `Session Groups`,
                    to: {name: 'admin.courses.session-groups.index', params: {courseId: this.$route.params.courseId}}
                },
                {
                    label: `Group`,
                    to: {
                        name: 'admin.courses.session-groups.show',
                        params: {
                            courseId: this.$route.params.courseId,
                            courseSessionGroupId: this.$route.params.courseSessionGroupId
                        }
                    }
                },
                {
                    label: `Records`, to: {name: this.$route.name, params: this.$route.params}
                },
            ];
        },
        status: {
            get() {
                return this.constraints.status ? parseInt(this.constraints.status) : null;
            },
            set(status) {
                this.$refs.pagination.getData(_.merge(this.constraints, {status, page: null}));
            },
        },
    },

    async beforeRouteEnter(to, from, next) {

        const data = await fetchAllData(to);
        next(vm => vm.setData(data));
    },

    async beforeRouteUpdate(to, from) {

        let data = {};

        if (to.params.courseSessionGroupId !== from.params.courseSessionGroupId) {
            data = await fetchAllData(to);
        } else {
            data.records = (await fetchRecords(to)).data;
        }

        this.setData(data);
    },
};

const fetchAllData = async to => {

    const data = await Promise.all([
        fetchRecords(to),
        axios.get(route('api.courses.show', to.params.courseId)),
    ])

    return {
        records: data[0].data,
        course: data[1].data
    };
}

const fetchRecords = async (to) => {
    return axios.get(route('api.course-sessions.records', to.params.courseSessionId), {
        params: to.query
    });
}
</script>
